/**
 * Analytics Events - High-level event functions
 * 
 * Each function corresponds to one event in the tracking plan.
 * Import these in components instead of using track() directly.
 * 
 * Benefits:
 * - Type safety (required props enforced)
 * - Self-documenting code
 * - Easy to find all places where an event is tracked
 * - Centralized validation
 */

import { track, trackRaw, setUserProperties, incrementUserProperty } from './amplitude';
import { EVENTS, PRODUCT_CATEGORY } from './constants';
import { createFlowId, getOrCreateFlowId, clearFlowId } from './flowId';
import { createGenerationId, clearGenerationId } from './generationId';
import { createMealSetId, clearMealSetId, getMealSetId } from './mealSetId';

// =============================================================================
// ACQUISITION EVENTS
// =============================================================================

/**
 * Track: Landing Viewed
 * When: Landing page loads
 */
export function trackLandingViewed({ referrer, utm_source, utm_medium, utm_campaign, utm_content, utm_term } = {}) {
  trackRaw(EVENTS.LANDING_VIEWED, {
    referrer: referrer || document.referrer || null,
    utm_source: utm_source || null,
    utm_medium: utm_medium || null,
    utm_campaign: utm_campaign || null,
    utm_content: utm_content || null,
    utm_term: utm_term || null,
  });
}

/**
 * Track: CTA Clicked
 * When: User clicks "Create my free meal set" or similar CTA
 * 
 * IMPORTANT: This creates a new flow_id
 */
export function trackCTAClicked({ cta_location, cta_text = null }) {
  // Create new flow_id for this journey
  createFlowId();
  
  track(EVENTS.CTA_CLICKED, {
    cta_location,
    cta_text,
  });
}

// =============================================================================
// WIZARD EVENTS
// =============================================================================

/**
 * Track: Wizard Started
 * When: User lands on /wizard
 */
export function trackWizardStarted({ entry_source, prefill_present = false }) {
  // Ensure flow_id exists (create if direct entry)
  getOrCreateFlowId();
  
  track(EVENTS.WIZARD_STARTED, {
    entry_source,
    prefill_present,
  });
}

/**
 * Track: Meal Set Requested
 * When: User clicks "Create Meals" (commitment moment)
 * 
 * IMPORTANT: This creates a new generation_id
 */
export function trackMealSetRequested({
  household_size,
  budget_category,
  budget_amount,
  meals_breakfast_per_day,
  meals_lunch_per_day,
  meals_dinner_per_day,
  cuisines_count,
  dietary_count,
  tries_remaining,
  primary_cuisine = null,
  has_dietary_restrictions = false,
  country,
  currency,
}) {
  // Create new generation_id for this attempt
  const generation_id = createGenerationId();
  
  track(EVENTS.MEAL_SET_REQUESTED, {
    generation_id,
    household_size,
    budget_category,
    budget_amount,
    meals_breakfast_per_day,
    meals_lunch_per_day,
    meals_dinner_per_day,
    cuisines_count,
    dietary_count,
    tries_remaining,
    primary_cuisine,
    has_dietary_restrictions,
    country,
    currency,
  });
  
  // Update user properties with preferences
  setUserProperties({
    household_size,
    budget_category,
    currency,
    cuisines_count,
    dietary_count,
  });
}

// =============================================================================
// GENERATION EVENTS
// =============================================================================

/**
 * Track: Meal Set Generation Succeeded
 * When: 9 meals successfully generated
 * 
 * IMPORTANT: This creates a new meal_set_id
 */
export function trackMealSetGenerationSucceeded({
  recipe_count,
  total_cost,
  generation_time_ms,
  max_storage_days = null,
  model = null,
  provider = null,
}) {
  // Create meal_set_id for successful generation
  const meal_set_id = createMealSetId();
  
  track(EVENTS.MEAL_SET_GENERATION_SUCCEEDED, {
    meal_set_id,
    recipe_count,
    total_cost,
    generation_time_ms,
    max_storage_days,
    model,
    provider,
  });
  
  // Increment user counter
  incrementUserProperty('total_meal_sets_created');
}

/**
 * Track: Meal Set Generation Failed
 * When: Generation fails
 */
export function trackMealSetGenerationFailed({
  error_stage,
  error_code,
  retry_count,
  http_status = null,
  provider = null,
  model = null,
}) {
  track(EVENTS.MEAL_SET_GENERATION_FAILED, {
    error_stage,
    error_code,
    retry_count,
    http_status,
    provider,
    model,
  });
}

/**
 * Track: Meal Plan Structure Invalid
 * When: AI returned a meal plan that is NOT 3 Breakfasts / 3 Lunches / 3 Dinners.
 * Fired on every validation failure (including the one that triggers a structure-retry).
 */
export function trackMealPlanStructureInvalid({
  breakfasts,
  lunches,
  dinners,
  unknown = 0,
  recipe_count = null,
  retry_attempt,
  failure_reason = null,
  parse_status = null,
  validation_source = null,
  model = null,
  provider = null,
  response_length_bucket = null,
  meal_type_sequence = null,
}) {
  track(EVENTS.MEAL_PLAN_STRUCTURE_INVALID, {
    breakfasts,
    lunches,
    dinners,
    unknown,
    recipe_count,
    retry_attempt,
    failure_reason,
    parse_status,
    validation_source,
    model,
    provider,
    response_length_bucket,
    meal_type_sequence,
    total: (breakfasts || 0) + (lunches || 0) + (dinners || 0),
  });
}

// =============================================================================
// CORE VALUE: RECIPE EVENTS
// =============================================================================

/**
 * Track: Recipe Viewed
 * When: User opens recipe drawer
 */
export function trackRecipeViewed({
  meal_type,
  position_in_meal_type,
  position_in_set,
  recipe_id = null,
}) {
  track(EVENTS.RECIPE_VIEWED, {
    meal_type,
    position_in_meal_type,
    position_in_set,
    recipe_id,
  });
}

/**
 * Track: Recipe Replaced
 * When: User replaces a meal
 */
export function trackRecipeReplaced({
  meal_type,
  position_in_meal_type,
  position_in_set,
  replacements_used_count,
  old_recipe_id = null,
  new_recipe_id = null,
  replacement_reason = null,
}) {
  track(EVENTS.RECIPE_REPLACED, {
    meal_type,
    position_in_meal_type,
    position_in_set,
    replacements_used_count,
    old_recipe_id,
    new_recipe_id,
    replacement_reason,
  });
  
  // Increment user counter
  incrementUserProperty('total_recipes_replaced');
}

// =============================================================================
// CORE VALUE: GROCERY EVENTS
// =============================================================================

/**
 * Track: Grocery List Viewed
 * When: User opens grocery list sidebar
 */
export function trackGroceryListViewed({
  products_count,
  total_cost,
  have_it_count,
  open_trigger,
  leftovers_count = 0,
}) {
  track(EVENTS.GROCERY_LIST_VIEWED, {
    products_count,
    total_cost,
    have_it_count,
    open_trigger,
    leftovers_count,
  });
}

/**
 * Track: Grocery Item Toggled
 * When: User toggles "I have it"
 */
export function trackGroceryItemToggled({
  toggle_state,
  product_category,
  product_id = null,
  price = null,
  toggle_source = null,
}) {
  // Normalize category to whitelist
  const normalizedCategory = normalizeToProductCategory(product_category);
  
  track(EVENTS.GROCERY_ITEM_TOGGLED, {
    toggle_state,
    product_category: normalizedCategory,
    product_id,
    price,
    toggle_source,
  });
  
  // Increment user counter
  incrementUserProperty('total_have_it_toggles');
}

/**
 * Normalize a category string to our whitelist
 */
function normalizeToProductCategory(category) {
  if (!category) return PRODUCT_CATEGORY.OTHER;
  
  const values = Object.values(PRODUCT_CATEGORY);
  if (values.includes(category)) {
    return category;
  }
  
  // Try to match
  const lower = category.toLowerCase();
  if (lower.includes('meat') || lower.includes('seafood')) return PRODUCT_CATEGORY.MEAT_SEAFOOD;
  if (lower.includes('produce') || lower.includes('vegetable') || lower.includes('fruit')) return PRODUCT_CATEGORY.PRODUCE;
  if (lower.includes('dairy') || lower.includes('egg')) return PRODUCT_CATEGORY.DAIRY_EGGS;
  if (lower.includes('grain') || lower.includes('pasta')) return PRODUCT_CATEGORY.GRAINS_PASTA;
  if (lower.includes('frozen')) return PRODUCT_CATEGORY.FROZEN;
  if (lower.includes('bakery') || lower.includes('bread')) return PRODUCT_CATEGORY.BAKERY;
  if (lower.includes('refrigerated')) return PRODUCT_CATEGORY.REFRIGERATED;
  if (lower.includes('pantry')) return PRODUCT_CATEGORY.PANTRY;
  if (lower.includes('condiment') || lower.includes('sauce')) return PRODUCT_CATEGORY.CONDIMENTS;
  
  return PRODUCT_CATEGORY.OTHER;
}

// =============================================================================
// CONVERSION EVENTS
// =============================================================================

/**
 * Track: Save Clicked
 * When: User clicks Save button
 */
export function trackSaveClicked({ context_action = null } = {}) {
  track(EVENTS.SAVE_CLICKED, {
    context_action,
  });
}

/**
 * Track: Auth Started
 * When: Auth modal opens
 */
export function trackAuthStarted({
  intent,
  methods_shown,
  default_method = null,
}) {
  track(EVENTS.AUTH_STARTED, {
    intent,
    methods_shown,
    default_method,
  });
}

/**
 * Track: Auth Completed
 * When: Successful signup/login
 */
export function trackAuthCompleted({
  method,
  is_new_user,
  user_id,
  is_guest_before = true,
}) {
  track(EVENTS.AUTH_COMPLETED, {
    method,
    is_new_user,
    is_guest_before,
  });
  
  // Update user properties
  setUserProperties({
    is_registered: true,
    is_guest: false,
  });
  
  if (is_new_user) {
    setUserProperties({
      signup_method: method,
    });
  }
}

/**
 * Track: Meal Set Saved
 * When: Meal set is saved to account
 */
export function trackMealSetSaved({
  recipe_count,
  products_count,
  have_it_count,
  total_cost,
  saved_from = null,
}) {
  track(EVENTS.MEAL_SET_SAVED, {
    recipe_count,
    products_count,
    have_it_count,
    total_cost,
    saved_from,
  });
  
  // Increment user counter
  incrementUserProperty('total_meal_sets_saved');
  
  // Flow is complete - clear flow_id
  clearFlowId();
}

// =============================================================================
// LIMITS & PAYWALL EVENTS
// =============================================================================

/**
 * Track: Limit Reached
 * When: User hits a limit
 */
export function trackLimitReached({
  limit_type,
  limit_value,
  context_action,
  tries_remaining = null,
  replacements_remaining = null,
}) {
  track(EVENTS.LIMIT_REACHED, {
    limit_type,
    limit_value,
    context_action,
    tries_remaining,
    replacements_remaining,
  });
}

/**
 * Track: Paywall Viewed
 * When: Paywall/upgrade prompt is shown
 */
export function trackPaywallViewed({
  trigger,
  paywall_type,
  context_action,
  tries_remaining = null,
  replacements_remaining = null,
}) {
  // Get IDs if they exist (may not exist if on landing)
  const meal_set_id = getMealSetId();
  
  track(EVENTS.PAYWALL_VIEWED, {
    trigger,
    paywall_type,
    context_action,
    tries_remaining,
    replacements_remaining,
    // These are optional for paywall
    meal_set_id,
  });
}

// =============================================================================
// RETENTION EVENTS
// =============================================================================

/**
 * Track: My Meals Viewed
 * When: User opens saved plans list
 */
export function trackMyMealsViewed({ saved_plans_count, days_since_last_plan = null }) {
  trackRaw(EVENTS.MY_MEALS_VIEWED, {
    saved_plans_count,
    days_since_last_plan,
  });
}

/**
 * Track: Saved Meal Set Opened
 * When: User opens a saved meal set
 */
export function trackSavedMealSetOpened({
  meal_set_id,
  plan_age_days,
  entry_source = null,
}) {
  trackRaw(EVENTS.SAVED_MEAL_SET_OPENED, {
    meal_set_id,
    plan_age_days,
    entry_source,
  });
}

// =============================================================================
// FLOW MANAGEMENT
// =============================================================================

/**
 * Start a new flow (clear all IDs)
 * Call this when user explicitly starts a new meal set creation
 */
export function startNewFlow() {
  clearFlowId();
  clearGenerationId();
  clearMealSetId();
  createFlowId();
}

/**
 * Clear all analytics IDs
 * Call this on logout or when explicitly needed
 */
export function clearAllAnalyticsIds() {
  clearFlowId();
  clearGenerationId();
  clearMealSetId();
}

// =============================================================================
// PROMO LANDING EVENTS
// =============================================================================

/**
 * Track: Promo Landing Viewed
 * When: User lands on /promo/:id
 */
export function trackPromoLandingViewed({ promo_meal_id, promo_meal_title, utm_source = null, utm_campaign = null }) {
  getOrCreateFlowId();
  trackRaw(EVENTS.PROMO_LANDING_VIEWED, {
    promo_meal_id,
    promo_meal_title,
    utm_source: utm_source || null,
    utm_campaign: utm_campaign || null,
    referrer: document.referrer || null,
  });
}

/**
 * Track: Promo CTA Clicked
 * When: User clicks "Create my meal plan" on promo landing page
 * IMPORTANT: Creates a new flow_id for the new funnel journey
 */
export function trackPromoCTAClicked({ promo_meal_id, promo_meal_title, cta_text = null }) {
  createFlowId();
  track(EVENTS.PROMO_CTA_CLICKED, {
    promo_meal_id,
    promo_meal_title,
    cta_text,
  });
}
